import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExpenses } from '../hooks/useExpenses';
import ExpenseList from './ExpenseList';
import ExpenseSummary from './ExpenseSummary';
import Tabs from '@/shared/components/ui/Tabs';
import './expenses.css';

/* ── Sub-tabs ─────────────────────────────────────────────────────── */

const SUB_TABS = ['Expenses', 'Summary', 'Ledger'] as const;

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function prevMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 2, 1);
  return d.toISOString().slice(0, 7);
}

function nextMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo, 1);
  return d.toISOString().slice(0, 7);
}

export default function ExpensesManagement() {
  const [sp] = useSearchParams();
  const [subTab, setSubTab] = useState<string>('Expenses');
  const [month, setMonth] = useState(currentMonth);
  const [categoryFilter, setCategoryFilter] = useState('');

  const { expenses, summary, categories, loading, saving, saveExpense, removeExpense } =
    useExpenses(month, categoryFilter || undefined);

  // Sync sub-tab with URL
  useEffect(() => {
    const v = sp.get('expTab');
    if (v && (SUB_TABS as readonly string[]).includes(v)) setSubTab(v);
  }, [sp]);

  return (
    <div className="exp-root">
      {/* Sub-tabs */}
      <Tabs
        tabs={[...SUB_TABS]}
        activeTab={subTab}
        onTabChange={setSubTab}
        urlKey="expTab"
      />

      {subTab === 'Expenses' && (
        <ExpenseList
          expenses={expenses}
          categories={categories}
          loading={loading}
          saving={saving}
          onSave={saveExpense}
          onDelete={removeExpense}
          categoryFilter={categoryFilter}
          onCategoryFilter={setCategoryFilter}
        />
      )}

      {subTab === 'Summary' && (
        <ExpenseSummary
          summary={summary}
          month={month}
          loading={loading}
          onPrevMonth={() => setMonth(prevMonth(month))}
          onNextMonth={() => setMonth(nextMonth(month))}
          onThisMonth={() => setMonth(currentMonth())}
        />
      )}

      {subTab === 'Ledger' && (
        <div className="exp-ledger-placeholder">
          Ledger view coming in P2 — chronological timeline grouped by date.
        </div>
      )}
    </div>
  );
}
